import { useEffect } from "react";
import type { Map as MapLibreMap } from "maplibre-gl";
import type { LosResult } from "./computeLos";

const SOURCE = "tactical-los";
const LINE = "tactical-los-line";
const BLOCK = "tactical-los-block";
const ENDS = "tactical-los-ends";

type Draft = {
  observer: { lng: number; lat: number } | null;
  target: { lng: number; lat: number } | null;
};

type Props = {
  map: MapLibreMap | null;
  draft: Draft;
  result: LosResult | null;
};

function buildCollection(
  draft: Draft,
  result: LosResult | null,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  if (result) {
    const coords = result.samples.map((s) => [s.lng, s.lat] as [number, number]);
    if (coords.length >= 2) {
      features.push({
        type: "Feature",
        properties: { role: "line", visible: result.visible ? 1 : 0 },
        geometry: { type: "LineString", coordinates: coords },
      });
    }
    if (result.blockAt) {
      features.push({
        type: "Feature",
        properties: { role: "block" },
        geometry: {
          type: "Point",
          coordinates: [result.blockAt.lng, result.blockAt.lat],
        },
      });
    }
  } else if (draft.observer && draft.target) {
    features.push({
      type: "Feature",
      properties: { role: "line", visible: 1 },
      geometry: {
        type: "LineString",
        coordinates: [
          [draft.observer.lng, draft.observer.lat],
          [draft.target.lng, draft.target.lat],
        ],
      },
    });
  } else if (draft.observer) {
    features.push({
      type: "Feature",
      properties: { role: "end" },
      geometry: {
        type: "Point",
        coordinates: [draft.observer.lng, draft.observer.lat],
      },
    });
  }
  if (draft.observer) {
    features.push({
      type: "Feature",
      properties: { role: "end" },
      geometry: {
        type: "Point",
        coordinates: [draft.observer.lng, draft.observer.lat],
      },
    });
  }
  if (draft.target) {
    features.push({
      type: "Feature",
      properties: { role: "end" },
      geometry: {
        type: "Point",
        coordinates: [draft.target.lng, draft.target.lat],
      },
    });
  }
  return { type: "FeatureCollection", features };
}

/** Overlay результата LOS. Не участвует в CAD-выборе. */
export function TacticalLosOverlay({ map, draft, result }: Props) {
  useEffect(() => {
    if (!map) return;

    const ensure = () => {
      if (!map.getSource(SOURCE)) {
        map.addSource(SOURCE, {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
      }
      if (!map.getLayer(LINE)) {
        map.addLayer({
          id: LINE,
          type: "line",
          source: SOURCE,
          filter: ["==", ["get", "role"], "line"],
          paint: {
            "line-color": [
              "case",
              ["==", ["get", "visible"], 1],
              "#16a34a",
              "#dc2626",
            ],
            "line-width": 3,
            "line-opacity": 0.95,
          },
        });
      }
      if (!map.getLayer(BLOCK)) {
        map.addLayer({
          id: BLOCK,
          type: "circle",
          source: SOURCE,
          filter: ["==", ["get", "role"], "block"],
          paint: {
            "circle-radius": 7,
            "circle-color": "#dc2626",
            "circle-stroke-width": 2,
            "circle-stroke-color": "#fff",
          },
        });
      }
      if (!map.getLayer(ENDS)) {
        map.addLayer({
          id: ENDS,
          type: "circle",
          source: SOURCE,
          filter: ["==", ["get", "role"], "end"],
          paint: {
            "circle-radius": 5,
            "circle-color": "#1d4ed8",
            "circle-stroke-width": 2,
            "circle-stroke-color": "#fff",
          },
        });
      }
    };

    const sync = () => {
      try {
        ensure();
        const src = map.getSource(SOURCE) as
          | { setData: (d: GeoJSON.FeatureCollection) => void }
          | undefined;
        src?.setData(buildCollection(draft, result));
      } catch {
        /* style not ready */
      }
    };

    if (map.isStyleLoaded()) sync();
    else map.once("load", sync);
    map.on("styledata", sync);
    sync();

    return () => {
      map.off("styledata", sync);
      try {
        if (map.getLayer(ENDS)) map.removeLayer(ENDS);
        if (map.getLayer(BLOCK)) map.removeLayer(BLOCK);
        if (map.getLayer(LINE)) map.removeLayer(LINE);
        if (map.getSource(SOURCE)) map.removeSource(SOURCE);
      } catch {
        /* disposed */
      }
    };
  }, [map, draft, result]);

  return null;
}
